import { useState, useEffect, type ReactNode } from 'react';
import type { Link } from '../../models/v2/profile';
import GlassCard from '../ui/v2/GlassCard';
import { RiPencilLine, RiLinkedinFill, RiGithubFill, RiLinksLine, RiComputerLine } from 'react-icons/ri';
import ProfileSidePanel from './ProfileSidePanel';
import { profileService } from '../../services/profileService';
import { useProfileMutation } from '../../context/ProfileContext';

interface Props {
    links: Link[];
    onUpdate?: () => void;
}

export default function PortfolioSection({ links, onUpdate }: Props) {
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    // Local state for form fields
    const [formData, setFormData] = useState({
        linkedin: '',
        github: '',
        portfolio: '',
        other: ''
    });

    const { mutate, isMutating } = useProfileMutation();

    // Map links to form fields when panel opens
    useEffect(() => {
        if (isPanelOpen) {
            const getLink = (platform: string) => links.find(l => l.platform === platform)?.url || '';
            setFormData({
                linkedin: getLink('linkedin'),
                github: getLink('github'),
                portfolio: getLink('portfolio'),
                other: getLink('other')
            });
        }
    }, [isPanelOpen, links]);

    const handleSave = () => {
        // Construct array
        const newLinks: Link[] = [];
        if (formData.linkedin) newLinks.push({ id: 'linkedin', platform: 'linkedin', url: formData.linkedin, label: 'LinkedIn' });
        if (formData.github) newLinks.push({ id: 'github', platform: 'github', url: formData.github, label: 'GitHub' });
        if (formData.portfolio) newLinks.push({ id: 'portfolio', platform: 'portfolio', url: formData.portfolio, label: 'Portfolio' });
        if (formData.other) newLinks.push({ id: 'other', platform: 'other', url: formData.other, label: 'Other' });

        mutate(newLinks, {
            mutationFn: (data: any) => profileService.updateLinks(data),
            onSuccess: () => {
                setIsPanelOpen(false);
                if (onUpdate) onUpdate();
            },
            successMessage: 'Links updated successfully'
        });
    };

    const getLinkDisplay = (platform: string) => {
        const link = links.find(l => l.platform === platform);
        const url = link?.url || '';
        if (!url) return '-';
        // Clean URL for display
        return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    };

    return (
        <GlassCard>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Portfolio & Links</h2>
                <button
                    onClick={() => setIsPanelOpen(true)}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                    <RiPencilLine size={20} />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <LinkDisplay
                    icon={<RiLinkedinFill size={24} />}
                    label="LinkedIn URL"
                    value={getLinkDisplay('linkedin')}
                    color="bg-[#0077B5]"
                />
                <LinkDisplay
                    icon={<RiGithubFill size={24} />}
                    label="GitHub URL"
                    value={getLinkDisplay('github')}
                    color="bg-[#333]"
                />
                <LinkDisplay
                    icon={<RiComputerLine size={24} />}
                    label="Portfolio URL"
                    value={getLinkDisplay('portfolio')}
                    color="bg-pink-500"
                />
                <LinkDisplay
                    icon={<RiLinksLine size={24} />}
                    label="Other URL"
                    value={getLinkDisplay('other')}
                    color="bg-cyan-500"
                />
            </div>

            <ProfileSidePanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                title="Edit Portfolio"
                onSave={handleSave}
                isSaving={isMutating}
            >
                <div className="space-y-6">
                    <InputGroup
                        label="LinkedIn"
                        icon={<RiLinkedinFill size={20} />}
                        value={formData.linkedin}
                        onChange={v => setFormData({ ...formData, linkedin: v })}
                        placeholder="https://www.linkedin.com/in/..."
                    />
                    <InputGroup
                        label="GitHub"
                        icon={<RiGithubFill size={20} />}
                        value={formData.github}
                        onChange={v => setFormData({ ...formData, github: v })}
                        placeholder="https://github.com/..."
                    />
                    <InputGroup
                        label="Portfolio"
                        icon={<RiComputerLine size={20} />}
                        value={formData.portfolio}
                        onChange={v => setFormData({ ...formData, portfolio: v })}
                        placeholder="https://yourportfolio.com"
                    />
                    <InputGroup
                        label="Other Link"
                        icon={<RiLinksLine size={20} />}
                        value={formData.other}
                        onChange={v => setFormData({ ...formData, other: v })}
                        placeholder="https://..."
                    />
                </div>
            </ProfileSidePanel>
        </GlassCard>
    );
}

interface LinkDisplayProps {
    icon: ReactNode;
    label: string;
    value: string;
    color: string;
}

function LinkDisplay({ icon, label, value, color }: LinkDisplayProps) {
    return (
        <div className="flex items-start gap-4">
            <div className={`p-2 rounded-lg text-white ${color} shrink-0`}>
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-500 mb-0.5">{label}</p>
                <p className="text-white font-medium truncate">{value}</p>
            </div>
        </div>
    );
}

interface InputGroupProps {
    label: string;
    icon?: ReactNode;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

function InputGroup({ label, icon, value, onChange, placeholder }: InputGroupProps) {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">{label}</label>
            <div className="flex items-center rounded-xl overflow-hidden border border-white/10 bg-black/20 focus-within:border-blue-500/50 focus-within:bg-black/40 transition-all duration-200 group">
                {icon && (
                    <div className="pl-4 pr-3 py-3 text-gray-500 group-focus-within:text-blue-400 transition-colors">
                        {icon}
                    </div>
                )}
                <input
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="flex-1 bg-transparent py-3 pr-4 text-white outline-none placeholder:text-gray-700 min-w-0 font-medium"
                />
            </div>
        </div>
    );
}
